import React, { useState } from "react";
import { Button, Input, Modal } from "../ui";
import { DataTable, Switch } from "./fields";
import { useAsync } from "../../hooks";
import { useToast } from "../../context/ToastContext";
import content from "../../services/api-content";
import "../../styles/content.css";

const toForm = (fields, item) => {
  const f = {};
  fields.forEach((fd) => {
    const v = item?.[fd.key];
    f[fd.key] = fd.type === "bool" ? !!v : fd.type === "lines" ? (Array.isArray(v) ? v.join("\n") : "") : fd.type === "date" ? (v ? String(v).slice(0, 10) : "") : v ?? (fd.type === "number" ? "" : fd.type === "select" && fd.required ? fd.options[0] : fd.default ?? "");
  });
  return f;
};
const fromForm = (fields, f) => {
  const d = {};
  for (const fd of fields) {
    const v = f[fd.key];
    if (fd.type === "number") {
      if (v === "" || v === null) { if (fd.required) throw new Error(`กรุณากรอก ${fd.label}`); d[fd.key] = null; continue; }
      if (!Number.isFinite(Number(v))) throw new Error(`${fd.label} ต้องเป็นตัวเลข`);
      d[fd.key] = Number(v);
    } else if (fd.type === "bool") d[fd.key] = !!v;
    else if (fd.type === "lines") d[fd.key] = String(v || "").split("\n").map((x) => x.trim()).filter(Boolean);
    else if (fd.type === "date") d[fd.key] = v ? new Date(`${v}T23:59:59`).toISOString() : null;
    else {
      const s = String(v ?? "").trim();
      if (fd.required && !s) throw new Error(`กรุณากรอก ${fd.label}`);
      d[fd.key] = fd.upper ? s.toUpperCase() : s;
    }
  }
  return d;
};

/** จัดการตารางตั้งค่า (admin.config.*) แบบตาราง + ฟอร์ม */
export default function ConfigManager({ table, columns, fields, noun, idLabel = "รหัส (ภาษาอังกฤษ ไม่ซ้ำ)", needsId = true, idUpper = false }) {
  const toast = useToast();
  const { data, loading, error, reload } = useAsync(() => content.admin.config.list(table), [table]);
  const [editing, setEditing] = useState(null); // { item|null }
  const [form, setForm] = useState({});
  const [idVal, setIdVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const open = (item) => { setEditing({ item }); setForm(toForm(fields, item)); setIdVal(item?.id || ""); setErr(""); };
  const save = async () => {
    setErr("");
    let d;
    try {
      d = fromForm(fields, form);
      const id = idUpper ? idVal.trim().toUpperCase() : idVal.trim();
      if (!editing.item && needsId && !id) throw new Error(`กรุณากรอก ${idLabel}`);
      if (!editing.item && needsId && !/^[A-Za-z0-9_.-]{1,40}$/.test(id)) throw new Error("รหัสใช้ได้เฉพาะ A-Z a-z 0-9 _ . -");
      if (editing.item) d.id = editing.item.id; else if (needsId) d.id = id;
    } catch (e) { return setErr(e.message); }
    setBusy(true);
    try {
      await content.admin.config.save(table, d);
      toast("บันทึกแล้ว", "success");
      setEditing(null);
      reload();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const toggleActive = async (item) => {
    try {
      if (item.isActive === false) await content.admin.config.save(table, { ...item, isActive: true });
      else {
        if (!window.confirm(`ปิดใช้งาน ${item.name || item.id}? (ไม่ลบข้อมูล เปิดกลับได้)`)) return;
        await content.admin.config.remove(table, item.id);
      }
      reload();
    } catch (e) { toast(e.message, "error"); }
  };
  const cols = [...columns,
    { key: "isActive", label: "สถานะ", render: (r) => <span className={`pill ${r.isActive === false ? "pill--bad" : "pill--ok"}`}>{r.isActive === false ? "ปิด" : "เปิด"}</span> },
    { key: "act", label: "", render: (r) => <div className="row" style={{ gap: 6 }}><Button size="sm" onClick={() => open(r)}>แก้ไข</Button><Button size="sm" variant="ghost" onClick={() => toggleActive(r)}>{r.isActive === false ? "เปิดใช้" : "ปิดใช้"}</Button></div> },
  ];
  return (
    <div>
      <div className="row-between mb-16"><span className="muted">{data ? `${data.length} รายการ` : ""}</span><Button variant="primary" onClick={() => open(null)}>+ เพิ่ม{noun}</Button></div>
      <DataTable columns={cols} rows={data || []} loading={loading} error={error} onRetry={reload} empty={`ยังไม่มี${noun}`} />
      <Modal open={!!editing} onClose={() => setEditing(null)} title={`${editing?.item ? "แก้ไข" : "เพิ่ม"}${noun}`} size="lg"
        footer={<><Button variant="ghost" onClick={() => setEditing(null)}>ยกเลิก</Button><Button variant="primary" onClick={save} disabled={busy}>{busy ? "กำลังบันทึก..." : "บันทึก"}</Button></>}>
        <div className="form-grid">
          {needsId ? <div className="full"><Input label={idLabel} value={idVal} disabled={!!editing?.item} onChange={(e) => setIdVal(e.target.value)} /></div> : null}
          {fields.map((fd) => {
            const set = (v) => setForm((f) => ({ ...f, [fd.key]: v }));
            if (fd.type === "bool") return <div key={fd.key} className="setting-row full"><div className="strong">{fd.label}</div><Switch label={fd.label} checked={form[fd.key]} onChange={set} /></div>;
            if (fd.type === "select") return <label key={fd.key} className="field"><span className="field__label">{fd.label}</span><select className="field__control" value={form[fd.key]} onChange={(e) => set(e.target.value)}>{!fd.required ? <option value="">-</option> : null}{fd.options.map((o) => <option key={o} value={o}>{o}</option>)}</select></label>;
            if (fd.type === "lines" || fd.type === "textarea") return <div key={fd.key} className="full"><Input multiline label={fd.label} hint={fd.hint} value={form[fd.key]} onChange={(e) => set(e.target.value)} /></div>;
            return <Input key={fd.key} label={fd.label} hint={fd.hint} type={fd.type === "number" ? "number" : fd.type === "date" ? "date" : "text"} step={fd.step} value={form[fd.key] ?? ""} onChange={(e) => set(e.target.value)} />;
          })}
        </div>
        {err ? <div className="error-text" role="alert">{err}</div> : null}
      </Modal>
    </div>
  );
}
